import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, money, errMsg, downloadFile } from "@/lib/api";
import { QuoteActions } from "@/components/QuoteActions";
import { ItemGridPicker } from "@/components/ItemGridPicker";
import { SearchPicker } from "@/components/SearchPicker";
import { QuoteLines, calcTotals } from "@/components/QuoteLines";
import { GstBadge } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Row = ({ label, value, strong, testid }) => (
  <div className={`flex justify-between py-1.5 ${strong ? "border-t border-white/20 pt-3 text-lg font-bold" : "text-sm"}`}>
    <span>{label}</span><span className="num" data-testid={testid}>{money(value)}</span>
  </div>
);

export default function QuoteBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState({ reference: "", notes: "", valid_until: "", status: "draft" });
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null);
  const isOrder = doc?.type === "order";

  useEffect(() => {
    if (!id) return;
    api.get(`/quotes/${id}`).then(({ data }) => {
      setCustomer(data.customer);
      setLines(data.lines);
      setNumber(data.number);
      setDoc(data);
      setForm({ reference: data.reference, notes: data.notes, valid_until: data.valid_until, status: data.status });
    }).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  const addItem = (it) => setLines([...lines, { item_id: it.id, sku: it.sku, name: it.name, description: it.description || "", qty: 1, unit_price: it.unit_price, cost_price: it.cost_price || 0, has_gst: it.has_gst, stock: it.stock }]);
  const t = calcTotals(lines);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (thenPdf) => {
    if (!customer) return toast.error("Select a customer");
    if (!lines.length) return toast.error("Add at least one item");
    if (lines.some((l) => !(Number(l.qty) > 0))) return toast.error("Quantities must be greater than 0");
    setBusy(true);
    const body = { ...form, customer_id: customer.id, lines: lines.map(({ stock, line_total, gst, ...l }) => ({ ...l, qty: Number(l.qty), unit_price: Number(l.unit_price), discount: Number(l.discount) || 0, discount_type: l.discount_type || "pct" })) };
    try {
      const { data } = id ? await api.put(`/quotes/${id}`, body) : await api.post("/quotes", body);
      toast.success(`Quote ${data.number} saved`);
      if (thenPdf) await downloadFile(`/quotes/${data.id}/pdf`, `${data.number}.pdf`);
      setDoc(data);
      if (!id) nav(`/sales/${data.id}`, { replace: true });
      return data;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Quote deleted");
    nav("/sales");
  };

  return (
    <div className="fade-up">
      <Link to="/sales" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#01195D]" data-testid="back-to-sales"><ArrowLeft className="h-4 w-4" /> Back to sales</Link>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-2xl font-extrabold tracking-tight text-[#0B1523] sm:text-3xl" data-testid="quote-title">{isOrder ? `Order ${doc.order_number}` : id ? `Quote ${number}` : "New quote"}</h1>
        <QuoteActions id={id} doc={doc} setDoc={setDoc} busy={busy} save={save} remove={remove} customerEmail={customer?.email} />
      </div>
      {isOrder && (
        <div className="mb-6 rounded-lg border border-[#01195D]/20 bg-[#01195D]/5 px-4 py-3 text-sm text-[#01195D]" data-testid="order-banner">
          Converted from quote <span className="num font-semibold">{number}</span> by {doc.converted_by} on {new Date(doc.converted_at).toLocaleDateString("en-AU")}. Stock has been deducted; this order is read-only.
        </div>
      )}
      {doc?.emailed_at && <p className="-mt-3 mb-5 text-xs text-slate-500" data-testid="quote-emailed-info">Last emailed to {doc.emailed_to} on {new Date(doc.emailed_at).toLocaleString("en-AU")}</p>}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px] [&>*]:min-w-0">
        <div className={`space-y-6 ${isOrder ? "pointer-events-none opacity-80" : ""}`}>
          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Customer</Label>
                <SearchPicker endpoint="/customers" testid="quote-customer-picker" className="mt-1.5 w-full" placeholder="Search customers…"
                  label={customer ? customer.name : "Select customer"} onSelect={setCustomer}
                  render={(c) => <div><div className="font-medium">{c.name}</div><div className="text-xs text-slate-500">{c.contact_name} {c.email}</div></div>} />
                {customer && <p className="mt-1.5 text-xs text-slate-500" data-testid="quote-customer-details">{[customer.contact_name, customer.email, customer.phone].filter(Boolean).join(" · ")}</p>}
              </div>
              <div><Label>Reference / PO</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" data-testid="quote-reference-input" /></div>
              <div><Label>Valid until</Label><Input type="date" value={form.valid_until} onChange={set("valid_until")} className="mt-1.5" data-testid="quote-valid-input" /></div>
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 p-4">
              <h3 className="w-full font-semibold text-[#0B1523] sm:w-auto">Line items</h3>
              <div className="sm:ml-auto"><ItemGridPicker lines={lines} setLines={setLines} /></div>
              <SearchPicker endpoint="/items" testid="quote-add-item-picker" placeholder="Search SKU or name…" label="+ Add item" onSelect={addItem}
                render={(it) => (
                  <div className="flex w-full items-center justify-between gap-3">
                    <div><div className="font-medium">{it.name}</div><div className="num text-xs text-slate-500">{it.sku} · Stock {it.stock}</div></div>
                    <div className="text-right"><div className="num text-sm">{money(it.unit_price)}</div><GstBadge on={it.has_gst} /></div>
                  </div>
                )} />
            </div>
            <div className="overflow-x-auto"><QuoteLines lines={lines} setLines={setLines} /></div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <Label>Notes (shown on PDF)</Label>
            <Textarea rows={3} value={form.notes} onChange={set("notes")} className="mt-1.5" data-testid="quote-notes-input" />
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg bg-[#01195D] p-5 text-white lg:sticky lg:top-6">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-300">Summary</div>
            <Row label="Subtotal (ex GST)" value={t.sub} testid="quote-subtotal" />
            <Row label="GST (10%)" value={t.gst} testid="quote-gst" />
            <Row label="Total (inc GST)" value={t.total} strong testid="quote-total" />
            {t.disc > 0 && <div className="mt-2 text-right text-xs text-[#7DD3FC]" data-testid="quote-discount-total">Includes discounts of {money(t.disc)}</div>}
            <div className="mt-5">
              <Label className="text-slate-300">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5 border-white/20 bg-white/10 text-white" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["draft", "sent", "accepted", "declined"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
